import { useCallback, useEffect, useState } from 'react';
import { Mail, CheckCircle, XCircle, Link2, RefreshCw, X, Search, Eye } from 'lucide-react';
import { FacturaPreviewModal } from './FacturasRecibidas';
import useToolbarResponsive from '../../hooks/useToolbarResponsive';
import toast from 'react-hot-toast';
import { gastosApi } from '../../utils/api';
import ConfirmModal from '../../components/ConfirmModal';
import CorreoProveedorModal from '../../components/gastos/CorreoProveedorModal';

/**
 * Gastos → Pendientes de rechazo al proveedor. Facturas que incumplen una regla que bloquea: se le pidió al
 * proveedor la NC que la anule. Cuando llega (se detecta sola por la referencia a la clave), se aceptan la
 * original y la NC; si no llega antes del plazo de Hacienda, se rechaza ante Hacienda. 25-oct-2026.
 * Una línea por factura; el clic abre el seguimiento completo; las acciones van en la barra (como Por Confirmar).
 */
const fecha = (d) => {
  if (!d) return '';
  const s = String(d).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s.split('-').reverse().join('/') : new Date(d).toLocaleDateString('es-CR', { timeZone: 'America/Costa_Rica' });
};
const fechaHora = (d) => d ? new Date(d).toLocaleString('es-CR', { timeZone: 'America/Costa_Rica', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
const dinero = (n, m) => `${m === 'USD' ? '$' : '₡'}${(Number(n) || 0).toLocaleString(m === 'USD' ? 'en-US' : 'es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Un solo estado por factura, en palabras claras: en qué va y qué sigue */
function estadoDe(f) {
  if (f.rechazoError) return ['Rechazo con error', 'border-red-400 text-red-700'];
  if (f.nc?.anulaTotal) return ['Lista para aceptar', 'border-green-400 text-green-700'];   // llegó la nota de crédito que la anula
  if (f.nc) return ['Revisar nota de crédito', 'border-amber-400 text-amber-700'];       // llegó, pero no anula todo
  if (f.correoError) return ['Correo con error', 'border-red-400 text-red-700'];
  if (!f.correoEnviadoAt) return ['Falta enviar correo', 'border-gray-300 text-gray-600'];
  return ['Esperando al proveedor', 'border-blue-300 text-blue-700'];
}
/** Avance del seguimiento: 1 no aceptada · 2 correo al proveedor · 3 nota de crédito · 4 respuesta a Hacienda */
function pasosDe(f) {
  const hechos = [true, !!f.correoEnviadoAt, !!f.nc?.anulaTotal, false];
  const error = !!(f.correoError && !f.correoEnviadoAt) || !!f.rechazoError || (!!f.nc && !f.nc.anulaTotal);
  return { hechos, error };
}
const colorVence = (h) => h < 0 || h <= 2 ? 'text-red-600 font-semibold' : h <= 5 ? 'text-amber-600 font-medium' : 'text-gray-800';

function BotonBarra({ icon: Icon, label, onClick, disabled, color }) {
  const cl = { azul: 'border-blue-400 text-blue-600 hover:bg-blue-50', verde: 'border-green-400 text-green-600 hover:bg-green-50', rojo: 'border-red-400 text-red-500 hover:bg-red-50', gris: 'border-gray-300 text-gray-700 hover:bg-gray-50' }[color];
  return (
    <button onClick={onClick} disabled={disabled}
      className={`flex flex-col items-center justify-center px-3 py-2 rounded-lg border text-xs font-medium min-w-[70px] transition-all ${cl} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
      <Icon className="h-5 w-5 mb-1" /><span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

function VincularNC({ pendiente, onClose, onListo }) {
  const [lista, setLista] = useState(null);
  useEffect(() => {
    gastosApi.ncCandidatas(pendiente.id).then(r => setLista(r.data?.data || [])).catch(err => { if (!err.response) toast.error('No se pudieron cargar las notas de crédito'); onClose(); });
  }, [pendiente.id]);
  const vincular = async (nc) => {
    try {
      const r = await gastosApi.vincularNC(pendiente.id, nc.id);
      toast.success(r.data?.data?.anulaTotal ? 'Nota de crédito vinculada' : 'Vinculada, pero no anula la factura completa');
      onListo();
    } catch (err) { if (!err.response) toast.error('No se pudo vincular'); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-3xl border-2 border-gray-200" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div><h3 className="text-lg font-semibold">Vincular nota de crédito</h3><p className="text-sm text-gray-500">{pendiente.proveedor} · <span className="font-mono">{pendiente.numero}</span> · {dinero(pendiente.total, pendiente.moneda)}</p></div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600 mb-2">Notas de crédito de este proveedor desde la fecha de la factura (el proveedor no puso la referencia):</p>
          {!lista ? <p className="text-sm text-gray-500">Buscando...</p> : lista.length === 0 ? <p className="text-sm text-gray-500">No hay notas de crédito de este proveedor.</p> : (
            <div className="border rounded-lg divide-y">
              {lista.map(nc => (
                <button key={nc.id} onClick={() => vincular(nc)} className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between gap-3 text-sm">
                  <span className="font-mono text-xs">{nc.numero_consecutivo}</span>
                  <span className="text-gray-500">{fecha(nc.fecha_emision)}</span>
                  <span className="font-medium">{dinero(nc.total_comprobante, nc.moneda)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex justify-end px-6 py-4 border-t"><button onClick={onClose} className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50">Cerrar</button></div>
      </div>
    </div>
  );
}

/** Seguimiento completo de una factura: qué pasó, qué falta y las acciones */
function Seguimiento({ f, onClose, acciones, onVer }) {
  const [estado, cl] = estadoDe(f);
  const Paso = ({ hecho, titulo, detalle, mal }) => (
    <li className="flex gap-3 py-2.5">
      <span className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center text-[11px] font-bold shrink-0 ${mal ? 'border-red-400 text-red-600' : hecho ? 'border-green-500 text-green-600' : 'border-gray-300 text-gray-400'}`}>{mal ? '✕' : hecho ? '✓' : ''}</span>
      <div className="min-w-0"><div className={`text-sm ${hecho || mal ? 'text-gray-900' : 'text-gray-500'}`}>{titulo}</div>{detalle && <div className="text-xs text-gray-500 break-words">{detalle}</div>}</div>
    </li>
  );
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-4xl border-2 border-gray-200" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 py-4 border-b">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900">Seguimiento</h3>
            <p className="text-sm text-gray-500 truncate">{f.proveedor} · <span className="font-mono">{f.numero}</span> · {fecha(f.fecha)} · <strong className="text-gray-800">{dinero(f.total, f.moneda)}</strong></p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-2 py-0.5 rounded-full border text-xs font-medium whitespace-nowrap ${cl}`}>{estado}</span>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="px-6 py-4 border-b md:border-b-0 md:border-r">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Qué pasó</div>
            <ol className="divide-y">
              <Paso hecho titulo={`No se aceptó: ${(f.etiquetas?.length ? f.etiquetas : ['incumple una regla']).join(', ').toLowerCase()}`} detalle={f.motivos.join(' ')} />
              <Paso hecho={!!f.correoEnviadoAt} mal={!f.correoEnviadoAt && !!f.correoError}
                titulo={f.correoEnviadoAt ? `Correo al proveedor${f.correoVeces > 1 ? ` (${f.correoVeces} veces)` : ''}` : 'Correo al proveedor sin enviar'}
                detalle={f.correoEnviadoAt ? `${fechaHora(f.correoEnviadoAt)} · ${f.correoPara || ''}` : f.correoError || ''} />
              <Paso hecho={!!f.nc?.anulaTotal} mal={!!f.nc && !f.nc.anulaTotal}
                titulo={f.nc ? `Nota de crédito ${f.nc.numero}` : 'Nota de crédito del proveedor: todavía no llega'}
                detalle={f.nc ? `${dinero(f.nc.total, f.moneda)} · ${f.nc.anulaTotal ? 'anula la factura completa' : 'no anula la factura completa'}${f.nc.vinculadaAMano ? ' · vinculada a mano' : ' · detectada por su referencia'}` : 'Se detecta sola cuando llega con la referencia a esta factura'} />
              <Paso hecho={false} mal={!!f.rechazoError} titulo={f.rechazoError ? 'El rechazo ante Hacienda dio error' : 'Respuesta a Hacienda'}
                detalle={f.rechazoError || (f.nc?.anulaTotal ? 'Lista para aceptar la factura y la NC' : 'Si no llega la NC, se rechaza antes del vencimiento')} />
            </ol>
          </div>
          <div className="px-6 py-4 space-y-3">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Plazo de Hacienda</div>
            <div className="border rounded-lg px-4 py-3">
              <div className={`text-2xl ${colorVence(f.habilesRestantes)}`}>{fecha(f.vence)}</div>
              <div className="text-sm text-gray-600">{f.habilesRestantes < 0 ? 'Venció' : f.habilesRestantes === 1 ? 'Es el último día hábil para responder' : `Faltan ${f.habilesRestantes} días hábiles`}</div>
              <div className="text-xs text-gray-500 mt-1">Si no se responde a Hacienda para esa fecha, la factura se presume aceptada.</div>
            </div>
            {(f.hasPdf || f.nc?.hasPdf) && (
              <div className="flex flex-wrap gap-2">
                {f.hasPdf && <button onClick={() => onVer(f.facturaId)} className="flex items-center gap-1 px-2.5 py-1 text-xs text-green-600 border border-green-300 rounded hover:bg-green-50"><Eye size={12} /> Ver factura</button>}
                {f.nc?.hasPdf && <button onClick={() => onVer(f.nc.id)} className="flex items-center gap-1 px-2.5 py-1 text-xs text-green-600 border border-green-300 rounded hover:bg-green-50"><Eye size={12} /> Ver nota de crédito</button>}
              </div>
            )}
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider pt-1">Acciones</div>
            <div className="flex flex-wrap gap-2">{acciones}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PendientesRechazo() {
  const [filas, setFilas] = useState(null);
  const [selId, setSelId] = useState(null);       // factura seleccionada (para la barra)
  const [detalle, setDetalle] = useState(null);   // seguimiento abierto
  const [correo, setCorreo] = useState(null);
  const [vincular, setVincular] = useState(null);
  const [confirmar, setConfirmar] = useState(null); // { tipo, fila }
  const [trabajando, setTrabajando] = useState(false);
  const [buscar, setBuscar] = useState('');
  const [preview, setPreview] = useState(null); // { factura, loading } — la misma vista previa de «Por Confirmar»
  const verDocumento = async (id) => {
    setPreview({ factura: null, loading: true });
    try { const r = await gastosApi.getFactura(id); setPreview({ factura: r.data?.data, loading: false }); }
    catch { setPreview(null); }
  };
  // Barra estándar: buscador a la izquierda, botones centrados en la pantalla (se corren solo si se traslapan)
  const { searchRef, buttonsRef } = useToolbarResponsive([buscar, selId]);

  const [errorCarga, setErrorCarga] = useState(null);
  const cargar = useCallback(() => gastosApi.getPendientesRechazo()
    .then(r => { const d = r.data?.data || []; setFilas(d); setDetalle(x => x ? d.find(y => y.id === x.id) || null : null); })
    .then(() => setErrorCarga(null))
    .catch(err => { setErrorCarga(err.response?.data?.error || 'No se pudo cargar la lista'); setFilas(x => x || []); }), []);
  useEffect(() => { cargar(); }, [cargar]);

  const sel = filas?.find(f => f.id === selId) || null;
  const q = buscar.trim().toLowerCase();
  const visibles = (filas || []).filter(f => !q || `${f.proveedor} ${f.numero}`.toLowerCase().includes(q));
  const ejecutar = async () => {
    const { tipo, fila } = confirmar;
    setConfirmar(null);
    setTrabajando(true);
    try {
      if (tipo === 'aceptar') { await gastosApi.aceptarConNC(fila.id); toast.success('Se enviaron a Hacienda la aceptación de la factura y de su nota de crédito'); }
      if (tipo === 'rechazar') { await gastosApi.rechazarPendiente(fila.id); toast.success('Se envió el rechazo a Hacienda y el aviso al proveedor'); }
      await cargar();
    } catch (err) {
      if (!err.response) toast.error('No se pudo completar');
    } finally {
      setTrabajando(false);
    }
  };
  const textos = {
    aceptar: ['Aceptar la factura y su nota de crédito', 'Se envía a Hacienda la aceptación de las dos. Se cancelan entre sí; la factura nueva del proveedor se confirma aparte en «Por Confirmar».', 'Aceptar las dos', 'success'],
    rechazar: ['Rechazar ante Hacienda', 'Se envía a Hacienda el rechazo de la factura con los motivos, y se le avisa al proveedor. No se puede deshacer.', 'Rechazar ante Hacienda', 'danger'],
  };
  const botones = (f, enModal = false) => {
    const off = !f || trabajando;
    return [
      ...(enModal ? [] : [<BotonBarra key="ver" icon={Eye} label="Ver" color="azul" disabled={!f?.hasPdf} onClick={() => verDocumento(f.facturaId)} />]),
      <BotonBarra key="c" icon={Mail} label={f?.correoEnviadoAt ? 'Reenviar correo' : 'Enviar correo'} color="azul" disabled={off} onClick={() => setCorreo(f)} />,
      f?.nc?.anulaTotal || !f
        ? <BotonBarra key="a" icon={CheckCircle} label="Aceptar con NC" color="verde" disabled={off || !f?.nc?.anulaTotal} onClick={() => setConfirmar({ tipo: 'aceptar', fila: f })} />
        : <BotonBarra key="v" icon={Link2} label="Vincular NC" color="gris" disabled={off} onClick={() => setVincular(f)} />,
      <BotonBarra key="r" icon={XCircle} label="Rechazar" color="rojo" disabled={off} onClick={() => setConfirmar({ tipo: 'rechazar', fila: f })} />,
      ...(enModal ? [] : [<span key="s" className="w-px h-10 bg-gray-200 mx-1" />, <BotonBarra key="u" icon={RefreshCw} label="Actualizar" color="gris" disabled={trabajando} onClick={cargar} />])
    ];
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border p-3 shrink-0 relative">
        <div ref={searchRef} className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input type="text" placeholder="Buscar proveedor..." value={buscar} onChange={e => setBuscar(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm w-52" />
          </div>
          {sel && <span className="text-sm font-medium text-indigo-600 whitespace-nowrap">1 seleccionada</span>}
        </div>
        <div ref={buttonsRef} className="flex items-center justify-center gap-2 flex-wrap">
          {botones(sel)}
        </div>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                <th className="px-3 py-3 w-10"></th>
                <th className="px-3 py-3">Fecha</th><th className="px-3 py-3">Proveedor</th><th className="px-3 py-3">No. Documento</th>
                <th className="px-3 py-3">Motivo</th><th className="px-3 py-3">Seguimiento</th><th className="px-3 py-3">Vence</th>
                <th className="px-3 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filas === null && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Cargando...</td></tr>}
              {errorCarga && <tr><td colSpan={8} className="px-4 py-8 text-center text-red-700">{errorCarga} · <button type="button" onClick={cargar} className="underline">Reintentar</button></td></tr>}
              {!errorCarga && filas?.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No hay facturas pendientes de rechazo</td></tr>}
              {filas && filas.length > 0 && visibles.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Ninguna coincide con la búsqueda</td></tr>}
              {visibles.map(f => {
                const [estado] = estadoDe(f);
                return (
                  <tr key={f.id} onClick={() => setDetalle(f)} className={`cursor-pointer hover:bg-gray-50 ${selId === f.id ? 'bg-indigo-50/60' : ''}`}>
                    <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selId === f.id} onChange={() => setSelId(selId === f.id ? null : f.id)} aria-label="Seleccionar" />
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{fecha(f.fecha)}</td>
                    <td className="px-3 py-2.5"><div className="max-w-[13rem] truncate" title={f.proveedor}>{f.proveedor}</div></td>
                    <td className="px-3 py-2.5 whitespace-nowrap font-mono text-xs">{f.numero}</td>
                    <td className="px-3 py-2.5"><div className="max-w-[15rem] truncate" title={f.motivos.join(' ')}>{(f.etiquetas?.length ? f.etiquetas : f.motivos).join(' · ')}</div></td>
                    <td className="px-3 py-2.5">
                      {(() => {
                        const { hechos, error } = pasosDe(f);
                        return (
                          <button type="button" onClick={e => { e.stopPropagation(); setDetalle(f); }} title={estado}
                            className="flex items-center gap-2 px-2 py-1 rounded-lg border border-gray-300 hover:bg-gray-50 text-xs text-gray-700 whitespace-nowrap" aria-label="Ver seguimiento">
                            <span className="flex gap-0.5">
                              {hechos.map((h, i) => <span key={i} className={`w-3 h-1.5 rounded-full ${h ? 'bg-green-500' : (error && i === hechos.indexOf(false)) ? 'bg-red-400' : 'bg-gray-200'}`} />)}
                            </span>
                            <span className="text-gray-500">›</span>
                          </button>
                        );
                      })()}
                    </td>
                    <td className={`px-3 py-2.5 whitespace-nowrap ${colorVence(f.habilesRestantes)}`} title={f.habilesRestantes < 0 ? 'Venció' : `Faltan ${f.habilesRestantes} días hábiles`}>{fecha(f.vence)}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap font-medium">{dinero(f.total, f.moneda)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {detalle && <Seguimiento f={detalle} onClose={() => setDetalle(null)} acciones={botones(detalle, true)} onVer={verDocumento} />}
      {preview && <div className="relative z-[70]"><FacturaPreviewModal factura={preview.factura} loading={preview.loading} onClose={() => setPreview(null)} /></div>}
      {correo && <CorreoProveedorModal pendienteId={correo.id} modo="enviar" onClose={() => setCorreo(null)} onListo={() => { setCorreo(null); cargar(); }} />}
      {vincular && <VincularNC pendiente={vincular} onClose={() => setVincular(null)} onListo={() => { setVincular(null); cargar(); }} />}
      <ConfirmModal isOpen={!!confirmar} onClose={() => setConfirmar(null)} onConfirm={ejecutar}
        title={confirmar ? textos[confirmar.tipo][0] : ''} message={confirmar ? `${confirmar.fila.proveedor} · ${confirmar.fila.numero}. ${textos[confirmar.tipo][1]}` : ''}
        confirmText={confirmar ? textos[confirmar.tipo][2] : ''} variant={confirmar ? textos[confirmar.tipo][3] : 'warning'} />
    </div>
  );
}
